import { defineStore } from 'pinia';
import type { Avalart } from '@/shared/Avalarten/types';
import {
  aktiveBuergschaftsarten,
  aktiveBuergschaftsartenDZBank,
  type BuergschaftsartOption,
  Bürgschaftsart,
} from '@/types/Bürgschaft.ts';
import { sortBy } from 'es-toolkit';
import { useSharedApi } from '@/shared/api/useSharedApi.ts';

interface StoreState {
  avalarten: Avalart[];
  hasDzBankContracts: boolean;
}

const SONSTIGE_KEY_SET = new Set<string>([
  Bürgschaftsart.SONSTIGE,
  Bürgschaftsart.SONSTIGEGARANTIE,
]);

const toOption =
  (labelSelector: (a: Avalart) => string) =>
  (a: Avalart): BuergschaftsartOption => ({
    id: a.id,
    key: a.externeId,
    label: labelSelector(a),
    externeId: a.externeId,
  });

const toOptionWithLongLabel = toOption((a) => a.langbezeichnung);

const sortWithSonstigeExternLast = (list: BuergschaftsartOption[]) =>
  sortBy(list, [
    (avalart) => SONSTIGE_KEY_SET.has(avalart.externeId),
    (avalart) => avalart.label.toLocaleLowerCase('de'),
  ]);

export const useAvalartenStore = defineStore('AvalartenStore', {
  state: (): StoreState => ({
    avalarten: [],
    hasDzBankContracts: false,
  }),

  actions: {
    async ladeAvalarten() {
      const response = await useSharedApi().sharedAvalPartnerKonfigurator.findeAlleAvalarten();
      this.avalarten = response;
    },
  },

  getters: {
    getAlle(state): Avalart[] {
      return state.avalarten;
    },

    getAlleImportierbaren(state): Avalart[] {
      return state.avalarten.filter((avalart) => avalart.manuellerImport === true);
    },

    allAvalartenOptions(state): BuergschaftsartOption[] {
      return sortWithSonstigeExternLast(state.avalarten.map(toOptionWithLongLabel));
    },

    standardAvalartenOptions(state): BuergschaftsartOption[] {
      const aktivSet = new Set(
        state.hasDzBankContracts ? aktiveBuergschaftsartenDZBank : aktiveBuergschaftsarten,
      );
      return sortWithSonstigeExternLast(
        state.avalarten.filter((a) => aktivSet.has(a.externeId)).map(toOptionWithLongLabel),
      );
    },

    manuellImportierbareAvalartenOptions(state): BuergschaftsartOption[] {
      return sortWithSonstigeExternLast(
        state.avalarten.filter((a) => a.manuellerImport).map(toOptionWithLongLabel),
      );
    },

    forTexteUsableBuergschaftsarten(state): BuergschaftsartOption[] {
      return sortWithSonstigeExternLast(
        state.avalarten
          .filter((a) => a.manuellerImport)
          .map(toOptionWithLongLabel)
          .filter((option) => aktiveBuergschaftsarten.includes(option.key))
          .sort((a, b) => a.label.localeCompare(b.label)),
      );
    },

    getById(state): (id: string) => Avalart | undefined {
      return (id: string) => state.avalarten.find((a) => a.id === id);
    },

    getKurzbezeichnungByExterneId(state): (id: string) => string | undefined {
      return (id: string) => state.avalarten.find((a) => a.externeId === id).kurzbezeichnung;
    },

    getLangbezeichnungByExterneId(state): (id: string) => string | undefined {
      return (id: string) => state.avalarten.find((a) => a.externeId === id).langbezeichnung;
    },
  },
});
